(() => {
  'use strict';

  if (window.BallonnenshopCustomerMailRewards) return;

  const LIST_ENDPOINT = '/v1/rewards/customer-mail';
  const SESSION_ENDPOINT = '/v1/session';
  const REQUEST_TIMEOUT_MS = 12_000;
  const FOLLOW_UP_DELAY_MS = 6_000;
  const MAX_FOLLOW_UP_ATTEMPTS = 3;
  const AUTO_REFRESH_INTERVAL_MS = 30_000;
  const MAX_REWARDS = 20;
  const ACCOUNT_TIME_ZONE = 'Europe/Amsterdam';
  const REWARD_AMOUNTS = new Set(['5.00', '15.00']);
  const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
  const CODE_PATTERN = /^[A-Z0-9]{20}$/u;
  const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
  const STATUSES = new Set([
    'ready',
    'claiming',
    'reconcile_required',
    'claimed'
  ]);
  const TRUSTED_API_HOSTS = new Set([
    'account.ballonnenshop.nl',
    'account-dev.ballonnenshop.nl'
  ]);

  class CustomerMailRewardUiError extends Error {
    constructor(code, status = 0) {
      super(code);
      this.name = 'CustomerMailRewardUiError';
      this.code = code;
      this.status = status;
    }
  }

  const isRecord = (value) =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);

  const exactKeys = (value, expected) => {
    if (!isRecord(value)) throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    const actual = Object.keys(value).sort();
    const wanted = [...expected].sort();
    if (
      actual.length !== wanted.length ||
      actual.some((key, index) => key !== wanted[index])
    ) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
  };

  const requiredTimestamp = (value) => {
    if (typeof value !== 'string' || value.length > 64) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    return parsed.toISOString();
  };

  const requiredExpiresOn = (value) => {
    if (typeof value !== 'string' || value.length !== 10) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match) throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    const year = Number.parseInt(match[1], 10);
    const month = Number.parseInt(match[2], 10);
    const day = Number.parseInt(match[3], 10);
    const date = new Date(Date.UTC(year, month - 1, day, 12));
    if (
      year < 2026 ||
      year > 2100 ||
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    return value;
  };

  const amsterdamDateKey = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: ACCOUNT_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    const part = (type) => parts.find((entry) => entry.type === type)?.value;
    const year = part('year');
    const month = part('month');
    const day = part('day');
    if (!year || !month || !day) {
      throw new CustomerMailRewardUiError('DATE_UNAVAILABLE');
    }
    return `${year}-${month}-${day}`;
  };

  const rewardIsActive = (reward, now = new Date()) =>
    reward.expiresOn >= amsterdamDateKey(now);

  const formattedExpiresOn = (expiresOn) => {
    const match = ISO_DATE_PATTERN.exec(requiredExpiresOn(expiresOn));
    const date = new Date(
      Date.UTC(
        Number.parseInt(match[1], 10),
        Number.parseInt(match[2], 10) - 1,
        Number.parseInt(match[3], 10),
        12
      )
    );
    return new Intl.DateTimeFormat('nl-NL', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const rewardAmountLabel = (amount) => {
    if (!REWARD_AMOUNTS.has(amount)) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    return `€${Number.parseInt(amount, 10)}`;
  };

  const validateGiftCard = (value, expectedAmount) => {
    exactKeys(value, [
      'amount',
      'code',
      'currencyCode',
      'issuedAt',
      'lastCharacters'
    ]);
    if (
      typeof value.code !== 'string' ||
      !CODE_PATTERN.test(value.code) ||
      typeof value.lastCharacters !== 'string' ||
      !/^[A-Z0-9]{4}$/u.test(value.lastCharacters) ||
      !value.code.endsWith(value.lastCharacters) ||
      !REWARD_AMOUNTS.has(value.amount) ||
      value.amount !== expectedAmount ||
      value.currencyCode !== 'EUR'
    ) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    return Object.freeze({
      code: value.code,
      lastCharacters: value.lastCharacters,
      amount: value.amount,
      currencyCode: 'EUR',
      issuedAt: requiredTimestamp(value.issuedAt)
    });
  };

  const validateReward = (value) => {
    exactKeys(value, [
      'campaignId',
      'createdAt',
      'entitlementId',
      'expiresOn',
      'giftCard',
      'revision',
      'reward',
      'status'
    ]);
    exactKeys(value.reward, ['amount', 'currencyCode']);
    if (
      typeof value.entitlementId !== 'string' ||
      !UUID_PATTERN.test(value.entitlementId) ||
      typeof value.campaignId !== 'string' ||
      !UUID_PATTERN.test(value.campaignId) ||
      !Number.isSafeInteger(value.revision) ||
      value.revision < 1 ||
      !REWARD_AMOUNTS.has(value.reward.amount) ||
      value.reward.currencyCode !== 'EUR' ||
      !STATUSES.has(value.status)
    ) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    const giftCard =
      value.status === 'claimed'
        ? validateGiftCard(value.giftCard, value.reward.amount)
        : value.giftCard === null
          ? null
          : (() => {
              throw new CustomerMailRewardUiError('INVALID_RESPONSE');
            })();
    return Object.freeze({
      entitlementId: value.entitlementId.toLowerCase(),
      campaignId: value.campaignId.toLowerCase(),
      revision: value.revision,
      status: value.status,
      reward: Object.freeze({
        amount: value.reward.amount,
        currencyCode: 'EUR'
      }),
      giftCard,
      expiresOn: requiredExpiresOn(value.expiresOn),
      createdAt: requiredTimestamp(value.createdAt)
    });
  };

  const validateRewardList = (value) => {
    exactKeys(value, ['rewards']);
    if (!Array.isArray(value.rewards) || value.rewards.length > MAX_REWARDS) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    const validatedRewards = value.rewards.map(validateReward);
    if (
      new Set(validatedRewards.map((reward) => reward.entitlementId)).size !==
      validatedRewards.length
    ) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE');
    }
    const rewards = validatedRewards.filter((reward) => rewardIsActive(reward));
    return Object.freeze({ rewards: Object.freeze(rewards) });
  };

  let modal = null;
  let overviewPanel = null;
  let voucherPanel = null;
  let homeContainer = null;
  let rewardContainer = null;
  let rewards = [];
  let loaded = false;
  let loadController = null;
  let observer = null;
  let bootstrapObserver = null;
  let refreshTimer = null;
  let clickListenerAttached = false;
  let lifecycleListenersAttached = false;
  const claimControllers = new Map();
  const followUpTimers = new Map();

  const apiBase = () => {
    const raw = modal?.dataset.bsAccountApiBase || '';
    let parsed;
    try {
      parsed = new URL(raw);
    } catch (_error) {
      throw new CustomerMailRewardUiError('API_UNAVAILABLE');
    }
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      parsed.port ||
      parsed.search ||
      parsed.hash ||
      !TRUSTED_API_HOSTS.has(parsed.hostname)
    ) {
      throw new CustomerMailRewardUiError('API_UNAVAILABLE');
    }
    return parsed.toString().replace(/\/$/u, '');
  };

  const readJson = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE', response.status);
    }
    const payload = await response.json();
    if (!isRecord(payload)) {
      throw new CustomerMailRewardUiError('INVALID_RESPONSE', response.status);
    }
    return payload;
  };

  const errorFromPayload = (payload, status) => {
    const code =
      isRecord(payload?.error) && typeof payload.error.code === 'string'
        ? payload.error.code
        : 'REQUEST_FAILED';
    return new CustomerMailRewardUiError(code, status);
  };

  const timedFetch = async (path, options = {}, externalSignal = null) => {
    const controller = new AbortController();
    const abortFromExternal = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', abortFromExternal, { once: true });
    }
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      return await window.fetch(apiBase() + path, {
        ...options,
        mode: 'cors',
        credentials: 'include',
        cache: 'no-store',
        redirect: 'error',
        signal: controller.signal
      });
    } catch (error) {
      if (controller.signal.aborted) {
        throw new CustomerMailRewardUiError('REQUEST_ABORTED');
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      externalSignal?.removeEventListener('abort', abortFromExternal);
    }
  };

  const accountIsOpen = () =>
    modal?.dataset.bsAuthState === 'authenticated' &&
    modal.getAttribute('aria-hidden') !== 'true';

  const vouchersAreVisible = () =>
    accountIsOpen() &&
    voucherPanel &&
    !voucherPanel.hidden;

  const element = (tagName, className, text) => {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const rewardMessage = (body, message, state) => {
    const output = element('p', 'bs-account__reward-voucher-message', message);
    output.dataset.bsCustomerMailRewardMessage = '';
    output.dataset.state = state;
    output.setAttribute('role', state === 'error' ? 'alert' : 'status');
    output.setAttribute('aria-live', 'polite');
    body.append(output);
    return output;
  };

  const rewardCard = (reward) => {
    const claimed = reward.status === 'claimed';
    const pending = reward.status === 'claiming';
    const reconciling = reward.status === 'reconcile_required';
    const amount = rewardAmountLabel(reward.reward.amount);
    const card = element(
      'article',
      'bs-account__reward-voucher-state ' +
        (claimed
          ? 'bs-account__reward-voucher-state--claimed'
          : 'bs-account__reward-voucher-state--eligible')
    );
    card.dataset.bsCustomerMailReward = reward.entitlementId;

    const visual = element(
      'span',
      'bs-account__reward-voucher-visual',
      claimed ? '✓' : '€'
    );
    visual.setAttribute('aria-hidden', 'true');
    card.append(visual);

    const body = element('div', 'bs-account__reward-voucher-body');
    body.append(element('p', 'bs-account__eyebrow', 'Speciaal voor jou'));
    body.append(
      element(
        'h2',
        '',
        claimed
          ? `Jouw ${amount}-cadeaubon`
          : pending
            ? 'Je cadeaubon wordt klaargezet'
            : reconciling
              ? 'We controleren je cadeaubon'
              : `Er staat ${amount} voor je klaar`
      )
    );
    body.append(
      element(
        'p',
        '',
        claimed
          ? 'Gebruik deze code tijdens het afrekenen.'
          : reconciling
            ? 'We zoeken eerst veilig na of Shopify de code al heeft aangemaakt.'
            : pending
              ? 'Je persoonlijke code wordt veilig aangemaakt.'
              : 'Claim je persoonlijke cadeaubon en gebruik de code tijdens het afrekenen.'
      )
    );
    body.append(
      element(
        'p',
        'bs-account__mail-reward-expiry',
        `Geldig t/m ${formattedExpiresOn(reward.expiresOn)}`
      )
    );

    if (claimed) {
      const codeRow = element('div', 'bs-account__reward-code-row');
      const code = element(
        'output',
        'bs-account__reward-code',
        reward.giftCard.code
      );
      code.setAttribute('aria-label', 'Cadeauboncode');
      code.tabIndex = 0;
      const copy = element(
        'button',
        'bs-account__button bs-account__button--secondary',
        'Code kopiëren'
      );
      copy.type = 'button';
      copy.dataset.bsCustomerMailRewardCopy = reward.entitlementId;
      codeRow.append(code, copy);
      body.append(codeRow);
    } else {
      const claim = element(
        'button',
        'bs-account__button',
        reconciling
          ? 'Cadeaubon controleren'
          : pending
            ? 'Status verversen'
            : `Claim mijn ${amount}`
      );
      claim.type = 'button';
      claim.dataset.bsCustomerMailRewardClaim = reward.entitlementId;
      body.append(claim);
    }
    card.append(body);
    card.append(element('strong', 'bs-account__reward-voucher-value', amount));
    return card;
  };

  const renderHome = () => {
    if (!homeContainer) return;
    homeContainer.replaceChildren();
    const reward = rewards[0];
    if (!reward) {
      homeContainer.hidden = true;
      homeContainer.removeAttribute('aria-label');
      return;
    }

    const amount = rewardAmountLabel(reward.reward.amount);
    const claimed = reward.status === 'claimed';
    const pending = reward.status === 'claiming';
    const reconciling = reward.status === 'reconcile_required';
    const visual = element('span', 'bs-account__mail-reward-home-icon', '€');
    visual.setAttribute('aria-hidden', 'true');

    const copy = element('span', 'bs-account__mail-reward-home-copy');
    copy.append(
      element('span', 'bs-account__overview-kicker', 'Een cadeautje voor jou'),
      element(
        'strong',
        '',
        claimed
          ? `Je ${amount}-cadeaubon is klaar voor gebruik`
          : pending
            ? 'Je persoonlijke code wordt klaargezet'
            : reconciling
              ? 'We controleren je cadeaubon'
              : `Je ${amount}-cadeaubon staat klaar`
      ),
      element(
        'small',
        '',
        claimed
          ? 'Bekijk je code en gebruik hem tijdens het afrekenen.'
          : 'Open Cadeaubonnen en claim je persoonlijke code.'
      ),
      element(
        'span',
        'bs-account__mail-reward-home-expiry',
        `Geldig t/m ${formattedExpiresOn(reward.expiresOn)}`
      )
    );

    const action = element('span', 'bs-account__mail-reward-home-action');
    action.append(
      element('span', '', 'Naar Cadeaubonnen'),
      element('span', 'bs-account__mail-reward-home-arrow', '→')
    );
    action.lastElementChild?.setAttribute('aria-hidden', 'true');

    homeContainer.append(visual, copy, action);
    homeContainer.setAttribute(
      'aria-label',
      `${claimed ? 'Bekijk' : 'Claim'} je ${amount}-cadeaubon. Geldig t/m ${formattedExpiresOn(reward.expiresOn)}.`
    );
    homeContainer.hidden = false;
  };

  const render = () => {
    if (rewardContainer) {
      rewardContainer.replaceChildren();
      rewardContainer.hidden = rewards.length === 0;
      rewards.forEach((reward) => rewardContainer.append(rewardCard(reward)));
    }
    renderHome();
  };

  const renderError = () => {
    if (homeContainer) {
      homeContainer.replaceChildren();
      homeContainer.hidden = true;
      homeContainer.removeAttribute('aria-label');
    }
    if (!rewardContainer) return;
    rewardContainer.hidden = false;
    const card = element('div', 'bs-account__reward-voucher-state');
    const visual = element('span', 'bs-account__reward-voucher-visual', '!');
    visual.setAttribute('aria-hidden', 'true');
    const body = element('div', 'bs-account__reward-voucher-body');
    body.append(element('p', 'bs-account__eyebrow', 'Tijdelijk niet beschikbaar'));
    body.append(element('h2', '', 'Je extra cadeaubonnen konden niet worden geladen'));
    body.append(element('p', '', 'Probeer het over een ogenblik nog een keer.'));
    const retry = element(
      'button',
      'bs-account__button bs-account__button--secondary',
      'Opnieuw proberen'
    );
    retry.type = 'button';
    retry.dataset.bsCustomerMailRewardsRetry = '';
    body.append(retry);
    card.append(visual, body, element('strong', 'bs-account__reward-voucher-value', '—'));
    rewardContainer.replaceChildren(card);
  };

  const clearFollowUps = () => {
    followUpTimers.forEach((timer) => window.clearTimeout(timer));
    followUpTimers.clear();
  };

  const clearRefreshTimer = () => {
    if (refreshTimer !== null) window.clearTimeout(refreshTimer);
    refreshTimer = null;
  };

  const scheduleRefresh = () => {
    clearRefreshTimer();
    if (!accountIsOpen()) return;
    refreshTimer = window.setTimeout(() => {
      refreshTimer = null;
      if (accountIsOpen()) void load({ force: true });
    }, AUTO_REFRESH_INTERVAL_MS);
  };

  const clear = () => {
    loadController?.abort();
    loadController = null;
    claimControllers.forEach((controller) => controller.abort());
    claimControllers.clear();
    clearFollowUps();
    clearRefreshTimer();
    rewards = [];
    loaded = false;
    render();
  };

  const load = async ({ force = false } = {}) => {
    if (!accountIsOpen()) return;
    if (loaded && !force) {
      render();
      scheduleRefresh();
      return;
    }
    loadController?.abort();
    const controller = new AbortController();
    loadController = controller;
    try {
      const response = await timedFetch(
        `${LIST_ENDPOINT}?_bs_refresh=${Date.now()}`,
        { headers: { Accept: 'application/json' } },
        controller.signal
      );
      const payload = await readJson(response);
      if (!response.ok) throw errorFromPayload(payload, response.status);
      const result = validateRewardList(payload);
      if (controller.signal.aborted) return;
      rewards = [...result.rewards];
      loaded = true;
      render();
    } catch (error) {
      if (controller.signal.aborted || error?.code === 'REQUEST_ABORTED') return;
      if (error?.status === 401 || error?.code === 'NOT_AUTHENTICATED') {
        clear();
        return;
      }
      if (error?.status === 404) {
        rewards = [];
        loaded = true;
        render();
        return;
      }
      loaded = false;
      renderError();
    } finally {
      if (loadController === controller) loadController = null;
      if (accountIsOpen()) scheduleRefresh();
    }
  };

  const sessionCsrf = async (signal) => {
    const response = await timedFetch(
      SESSION_ENDPOINT,
      { headers: { Accept: 'application/json' } },
      signal
    );
    const payload = await readJson(response);
    if (
      !response.ok ||
      payload.authenticated !== true ||
      typeof payload.csrfToken !== 'string' ||
      payload.csrfToken.length < 16 ||
      payload.csrfToken.length > 256
    ) {
      throw new CustomerMailRewardUiError('NOT_AUTHENTICATED', 401);
    }
    return payload.csrfToken;
  };

  const replaceReward = (reward) => {
    const index = rewards.findIndex(
      (candidate) => candidate.entitlementId === reward.entitlementId
    );
    if (index < 0) return false;
    if (!rewardIsActive(reward)) {
      rewards = rewards.filter(
        (candidate) => candidate.entitlementId !== reward.entitlementId
      );
      render();
      return true;
    }
    rewards = rewards.map((candidate, currentIndex) =>
      currentIndex === index ? reward : candidate
    );
    render();
    return true;
  };

  const scheduleFollowUp = (entitlementId, idempotencyKey, attempt) => {
    const previous = followUpTimers.get(entitlementId);
    if (previous) window.clearTimeout(previous);
    if (attempt >= MAX_FOLLOW_UP_ATTEMPTS) return;
    const timer = window.setTimeout(() => {
      followUpTimers.delete(entitlementId);
      if (vouchersAreVisible()) {
        void claim(entitlementId, idempotencyKey, attempt + 1);
      }
    }, FOLLOW_UP_DELAY_MS);
    followUpTimers.set(entitlementId, timer);
  };

  const claim = async (
    entitlementId,
    idempotencyKey = window.crypto.randomUUID(),
    attempt = 0
  ) => {
    if (
      !vouchersAreVisible() ||
      !UUID_PATTERN.test(entitlementId) ||
      !UUID_PATTERN.test(idempotencyKey) ||
      claimControllers.has(entitlementId)
    ) {
      return;
    }
    const controller = new AbortController();
    claimControllers.set(entitlementId, controller);
    const card = rewardContainer?.querySelector(
      `[data-bs-customer-mail-reward="${entitlementId}"]`
    );
    const button = card?.querySelector('[data-bs-customer-mail-reward-claim]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Veilig controleren…';
    }
    try {
      const csrfToken = await sessionCsrf(controller.signal);
      const response = await timedFetch(
        `${LIST_ENDPOINT}/${encodeURIComponent(entitlementId)}/claim`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-BS-CSRF-Token': csrfToken,
            'X-BS-Idempotency-Key': idempotencyKey
          },
          body: '{}'
        },
        controller.signal
      );
      const payload = await readJson(response);
      if (!response.ok) throw errorFromPayload(payload, response.status);
      const result = validateReward(payload);
      if (!replaceReward(result)) {
        loaded = false;
        await load({ force: true });
      }
      if (['claiming', 'reconcile_required'].includes(result.status)) {
        scheduleFollowUp(entitlementId, idempotencyKey, attempt);
      }
    } catch (error) {
      if (controller.signal.aborted || error?.code === 'REQUEST_ABORTED') return;
      if (error?.status === 401 || error?.code === 'NOT_AUTHENTICATED') {
        clear();
        return;
      }
      if (card) {
        const body = card.querySelector('.bs-account__reward-voucher-body');
        const prior = body?.querySelector('[data-bs-customer-mail-reward-message]');
        prior?.remove();
        if (body) {
          rewardMessage(
            body,
            'Je cadeaubon kon nu niet veilig worden gecontroleerd. Probeer het zo nog eens.',
            'error'
          );
        }
      }
    } finally {
      if (claimControllers.get(entitlementId) === controller) {
        claimControllers.delete(entitlementId);
      }
      const currentButton = rewardContainer?.querySelector(
        `[data-bs-customer-mail-reward="${entitlementId}"] [data-bs-customer-mail-reward-claim]`
      );
      if (currentButton) {
        currentButton.disabled = false;
        currentButton.setAttribute('aria-busy', 'false');
      }
    }
  };

  const copy = async (entitlementId) => {
    const reward = rewards.find(
      (candidate) =>
        candidate.entitlementId === entitlementId &&
        candidate.status === 'claimed'
    );
    const card = rewardContainer?.querySelector(
      `[data-bs-customer-mail-reward="${entitlementId}"]`
    );
    const body = card?.querySelector('.bs-account__reward-voucher-body');
    if (!reward?.giftCard || !body) return;
    body.querySelector('[data-bs-customer-mail-reward-message]')?.remove();
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') {
        throw new Error('CLIPBOARD_UNAVAILABLE');
      }
      await navigator.clipboard.writeText(reward.giftCard.code);
      rewardMessage(body, 'Code gekopieerd.', 'success');
    } catch (_error) {
      rewardMessage(
        body,
        'Kopiëren lukte niet. Selecteer de code en kopieer hem handmatig.',
        'error'
      );
      card.querySelector('.bs-account__reward-code')?.focus({ preventScroll: true });
    }
  };

  const onClick = (event) => {
    const claimButton = event.target.closest?.('[data-bs-customer-mail-reward-claim]');
    if (claimButton && rewardContainer?.contains(claimButton)) {
      void claim(claimButton.dataset.bsCustomerMailRewardClaim || '');
      return;
    }
    const copyButton = event.target.closest?.('[data-bs-customer-mail-reward-copy]');
    if (copyButton && rewardContainer?.contains(copyButton)) {
      void copy(copyButton.dataset.bsCustomerMailRewardCopy || '');
      return;
    }
    const retry = event.target.closest?.('[data-bs-customer-mail-rewards-retry]');
    if (retry && rewardContainer?.contains(retry)) {
      void load({ force: true });
      return;
    }
    if (
      event.target.closest?.(
        '[data-bs-account-view="vouchers"], ' +
          '[data-bs-account-mobile-view="vouchers"], ' +
          '[data-bs-account-view-target="vouchers"]'
      )
    ) {
      window.setTimeout(() => void load(), 0);
    }
  };

  const refreshForState = () => {
    if (accountIsOpen()) {
      void load();
    } else if (
      modal?.dataset.bsAuthState !== 'authenticated' ||
      modal?.getAttribute('aria-hidden') === 'true'
    ) {
      clear();
    }
  };

  const attachLifecycleListeners = () => {
    if (lifecycleListenersAttached) return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && accountIsOpen()) {
        void load({ force: true });
      }
    });
    window.addEventListener('online', () => {
      if (accountIsOpen()) void load({ force: true });
    });
    lifecycleListenersAttached = true;
  };

  const stopBootstrapObserver = () => {
    bootstrapObserver?.disconnect();
    bootstrapObserver = null;
  };

  const observeForAccountModal = () => {
    if (bootstrapObserver || !document.documentElement) return;
    bootstrapObserver = new MutationObserver(() => {
      start();
    });
    bootstrapObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  const start = () => {
    if (
      modal?.isConnected &&
      homeContainer?.isConnected &&
      rewardContainer?.isConnected
    ) {
      refreshForState();
      return true;
    }
    const nextModal = document.querySelector('[data-bs-account-modal]');
    const nextOverviewPanel =
      nextModal?.querySelector('[data-bs-account-panel="overview"]') || null;
    const nextVoucherPanel =
      nextModal?.querySelector('[data-bs-account-panel="vouchers"]') || null;
    if (!nextModal || !nextOverviewPanel || !nextVoucherPanel) {
      observeForAccountModal();
      return false;
    }

    if (modal && modal !== nextModal) clear();
    modal = nextModal;
    overviewPanel = nextOverviewPanel;
    voucherPanel = nextVoucherPanel;
    stopBootstrapObserver();

    homeContainer =
      overviewPanel.querySelector('[data-bs-customer-mail-rewards-home]') || null;
    if (!homeContainer) {
      homeContainer = element('button', 'bs-account__mail-reward-home');
      homeContainer.type = 'button';
      homeContainer.dataset.bsCustomerMailRewardsHome = '';
      homeContainer.dataset.bsAccountViewTarget = 'vouchers';
      homeContainer.hidden = true;
      const overviewGrid = overviewPanel.querySelector('.bs-account__overview-grid');
      if (overviewGrid) overviewGrid.before(homeContainer);
      else overviewPanel.append(homeContainer);
    }

    rewardContainer =
      voucherPanel.querySelector('[data-bs-customer-mail-rewards]') || null;
    if (!rewardContainer) {
      rewardContainer = element('div', 'bs-account__reward-voucher');
      rewardContainer.dataset.bsCustomerMailRewards = '';
      rewardContainer.style.display = 'grid';
      rewardContainer.style.gap = '16px';
      rewardContainer.hidden = true;
      const calendarReward = voucherPanel.querySelector('[data-bs-voucher-reward]');
      if (calendarReward) calendarReward.before(rewardContainer);
      else voucherPanel.append(rewardContainer);
    }

    const intro = voucherPanel.querySelector('.bs-account__page-header p:last-child');
    if (intro) {
      intro.textContent =
        'Hier staan je persoonlijke cadeaubonnen en de beloningen die je zelf hebt verdiend.';
    }
    if (!clickListenerAttached) {
      document.addEventListener('click', onClick);
      clickListenerAttached = true;
    }
    attachLifecycleListeners();
    observer?.disconnect();
    observer = new MutationObserver(refreshForState);
    observer.observe(modal, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'data-bs-auth-state']
    });
    observer.observe(voucherPanel, {
      attributes: true,
      attributeFilter: ['hidden']
    });
    observer.observe(overviewPanel, {
      attributes: true,
      attributeFilter: ['hidden']
    });
    refreshForState();
    return true;
  };

  window.BallonnenshopCustomerMailRewards = Object.freeze({ start });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }
})();
